import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiClient } from '../App';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { 
  Search, 
  Plus, 
  Phone, 
  Calendar,
  ChevronRight,
  Download,
  Filter
} from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import NewPatientForm from '../components/forms/NewPatientForm';

const PatientsPage = () => {
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showNewPatient, setShowNewPatient] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const tagFilter = searchParams.get('tag') || '';

  useEffect(() => {
    fetchPatients();
  }, [search, tagFilter]);

  const fetchPatients = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (tagFilter) params.append('tag', tagFilter);
      
      const response = await apiClient.get(`/patients?${params}`);
      setPatients(response.data);
    } catch (error) {
      toast.error('Failed to load patients');
    } finally {
      setLoading(false);
    }
  };

  const handleTagFilter = (value) => {
    if (value === 'all') {
      searchParams.delete('tag');
    } else {
      searchParams.set('tag', value);
    }
    setSearchParams(searchParams);
  };

  const PatientAvatar = ({ photo, name }) => (
    photo ? (
      <img 
        src={`data:image/png;base64,${photo}`} 
        alt={name}
        className="w-12 h-12 rounded-xl object-cover border-2 border-white shadow-sm"
      />
    ) : (
      <div className="w-12 h-12 rounded-xl bg-primary flex items-center justify-center text-primary-foreground font-medium text-lg">
        {name?.charAt(0) || '?'}
      </div>
    )
  );

  const getTagBadge = (tag) => {
    const styles = {
      regular: 'bg-secondary text-secondary-foreground',
      pro_bono: 'bg-primary/10 text-primary',
      discount: 'bg-accent/10 text-accent',
    };
    const labels = {
      regular: 'Regular',
      pro_bono: 'Pro Bono',
      discount: 'Discount',
    };
    return (
      <Badge className={styles[tag] || styles.regular}>
        {labels[tag] || tag}
      </Badge>
    );
  };

  const downloadPatientCard = async (patientId) => {
    try {
      const response = await apiClient.get(`/patients/${patientId}/card-pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `patient_card_${patientId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Patient card downloaded');
    } catch (error) {
      toast.error('Failed to download patient card');
    }
  };

  return (
    <div className="space-y-6" data-testid="patients-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold">Patients</h1>
          <p className="text-muted-foreground text-sm mt-1">
            {patients.length} total patients
          </p>
        </div>
        <Button 
          onClick={() => setShowNewPatient(true)}
          className="rounded-full"
          data-testid="add-patient-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Patient
        </Button>
      </div>

      {/* Search & Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search by name, mobile, or Patient ID..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 h-11"
            data-testid="patient-search"
          />
        </div>
        <Select value={tagFilter || 'all'} onValueChange={handleTagFilter}>
          <SelectTrigger className="w-full sm:w-40" data-testid="tag-filter">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Filter by tag" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Tags</SelectItem>
            <SelectItem value="regular">Regular</SelectItem>
            <SelectItem value="pro_bono">Pro Bono</SelectItem>
            <SelectItem value="discount">Discount</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Patient List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : patients.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">No patients found</p>
            <Button 
              variant="outline" 
              className="mt-4"
              onClick={() => setShowNewPatient(true)}
            >
              Add your first patient
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {patients.map((patient, index) => (
            <Card 
              key={patient.patient_id}
              className="card-hover cursor-pointer animate-fade-in"
              style={{ animationDelay: `${index * 0.05}s` }}
              onClick={() => navigate(`/patients/${patient.patient_id}`)}
              data-testid={`patient-card-${patient.patient_id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  <PatientAvatar photo={patient.photo_data} name={patient.full_name} />
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-medium truncate">{patient.full_name}</h3>
                      {getTagBadge(patient.tag)}
                    </div>
                    <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Phone className="w-3 h-3" />
                        {patient.mobile}
                      </span>
                      {patient.birthday && (
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {patient.birthday}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      ID: {patient.patient_id}
                    </p>
                  </div>

                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={(e) => {
                        e.stopPropagation();
                        downloadPatientCard(patient.patient_id);
                      }}
                      data-testid={`download-card-${patient.patient_id}`}
                    >
                      <Download className="w-4 h-4" />
                    </Button>
                    <ChevronRight className="w-5 h-5 text-muted-foreground" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* New Patient Dialog */}
      <Dialog open={showNewPatient} onOpenChange={setShowNewPatient}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add New Patient</DialogTitle>
          </DialogHeader>
          <NewPatientForm 
            onSuccess={() => {
              setShowNewPatient(false);
              fetchPatients();
            }}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PatientsPage;
